/**
 * Technical indicators.
 *
 * Every indicator produces Float64Arrays index-aligned with the kline series,
 * NaN-filled where the indicator has no value yet (the warm-up window). The
 * renderer treats NaN as "pen up", so a warm-up period simply does not draw —
 * no special-casing at the draw site, and no off-by-one between an indicator's
 * first valid sample and the candle it belongs to, which is the classic way
 * these end up plotted one bar to the left of the truth.
 *
 * Results are cached against the series' `seq`, so an indicator recomputes
 * once per data change rather than once per frame.
 */

import type { KlineSeries } from './klines';
import { C } from '../styles/palette';

export type IndicatorKind = 'sma' | 'ema' | 'bb' | 'vwap' | 'rsi' | 'macd' | 'atr';
export type Pane = 'price' | 'sub';

export interface IndicatorConfig {
  id: string;
  kind: IndicatorKind;
  params: Record<string, number>;
  color: string;
  visible: boolean;
}

export interface IndicatorLine {
  key: string;
  values: Float64Array;
  color: string;
  width: number;
  dashed?: boolean;
}

export interface IndicatorBand {
  upper: string;
  lower: string;
  fill: string;
}

export interface IndicatorHistogram {
  values: Float64Array;
  upColor: string;
  downColor: string;
  upFade: string;
  downFade: string;
}

export interface IndicatorOutput {
  id: string;
  kind: IndicatorKind;
  label: string;
  pane: Pane;
  lines: IndicatorLine[];
  band?: IndicatorBand;
  histogram?: IndicatorHistogram;
  /** Fixed sub-pane range, or null to fit the visible data. */
  range: [number, number] | null;
  /** Horizontal reference levels drawn in the sub-pane. */
  guides?: number[];
  /** Zero line for oscillators that swing either side of it. */
  zeroLine?: boolean;
}

export interface ParamSpec {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
}

export interface IndicatorDef {
  label: string;
  pane: Pane;
  defaults: Record<string, number>;
  params: ParamSpec[];
  /** Short description shown in the add-indicator menu. */
  hint: string;
}

export const INDICATOR_DEFS: Record<IndicatorKind, IndicatorDef> = {
  sma: {
    label: 'SMA',
    pane: 'price',
    defaults: { period: 20 },
    params: [{ key: 'period', label: 'Period', min: 2, max: 400, step: 1 }],
    hint: 'Simple moving average of closes',
  },
  ema: {
    label: 'EMA',
    pane: 'price',
    defaults: { period: 50 },
    params: [{ key: 'period', label: 'Period', min: 2, max: 400, step: 1 }],
    hint: 'Exponentially weighted moving average',
  },
  bb: {
    label: 'BB',
    pane: 'price',
    defaults: { period: 20, mult: 2 },
    params: [
      { key: 'period', label: 'Period', min: 2, max: 200, step: 1 },
      { key: 'mult', label: 'Std dev', min: 0.5, max: 5, step: 0.5 },
    ],
    hint: 'Bollinger Bands — SMA with standard-deviation envelope',
  },
  vwap: {
    label: 'VWAP',
    pane: 'price',
    defaults: { anchor: 0 },
    params: [{ key: 'anchor', label: 'Anchor 0=day 1=week', min: 0, max: 1, step: 1 }],
    hint: 'Volume-weighted average price, reset each session',
  },
  rsi: {
    label: 'RSI',
    pane: 'sub',
    defaults: { period: 14 },
    params: [{ key: 'period', label: 'Period', min: 2, max: 100, step: 1 }],
    hint: 'Relative Strength Index (Wilder)',
  },
  macd: {
    label: 'MACD',
    pane: 'sub',
    defaults: { fast: 12, slow: 26, signal: 9 },
    params: [
      { key: 'fast', label: 'Fast', min: 2, max: 100, step: 1 },
      { key: 'slow', label: 'Slow', min: 3, max: 200, step: 1 },
      { key: 'signal', label: 'Signal', min: 2, max: 100, step: 1 },
    ],
    hint: 'Moving average convergence/divergence with histogram',
  },
  atr: {
    label: 'ATR',
    pane: 'sub',
    defaults: { period: 14 },
    params: [{ key: 'period', label: 'Period', min: 2, max: 100, step: 1 }],
    hint: 'Average True Range (Wilder) — realised volatility',
  },
};

let idCounter = 0;
export function createIndicator(kind: IndicatorKind, colorIndex = 0): IndicatorConfig {
  const def = INDICATOR_DEFS[kind];
  return {
    id: `${kind}-${Date.now().toString(36)}-${idCounter++}`,
    kind,
    params: { ...def.defaults },
    color: C.series[colorIndex % C.series.length],
    visible: true,
  };
}

export function indicatorLabel(cfg: IndicatorConfig): string {
  const def = INDICATOR_DEFS[cfg.kind];
  switch (cfg.kind) {
    case 'sma':
    case 'ema':
    case 'rsi':
    case 'atr':
      return `${def.label} ${cfg.params.period}`;
    case 'bb':
      return `${def.label} ${cfg.params.period}/${cfg.params.mult}`;
    case 'macd':
      return `${def.label} ${cfg.params.fast},${cfg.params.slow},${cfg.params.signal}`;
    case 'vwap':
      return cfg.params.anchor === 1 ? 'VWAP W' : 'VWAP D';
  }
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function filled(n: number): Float64Array {
  const a = new Float64Array(n);
  a.fill(NaN);
  return a;
}

/** Rolling simple mean. Uses a running sum, so cost is O(n) not O(n*period). */
export function sma(src: Float64Array, n: number, period: number, out?: Float64Array): Float64Array {
  const res = out && out.length === n ? out : filled(n);
  if (out) res.fill(NaN);
  if (period < 1 || n < period) return res;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += src[i];
    if (i >= period) sum -= src[i - period];
    if (i >= period - 1) res[i] = sum / period;
  }
  return res;
}

/**
 * Exponential moving average, seeded with the SMA of the first `period`
 * samples. Seeding from the first close instead (the naive approach) leaves a
 * visible hook at the left edge that takes several periods to decay.
 */
export function ema(src: Float64Array, n: number, period: number, out?: Float64Array): Float64Array {
  const res = out && out.length === n ? out : filled(n);
  if (out) res.fill(NaN);
  if (period < 1 || n < period) return res;
  const k = 2 / (period + 1);
  let seed = 0;
  for (let i = 0; i < period; i++) seed += src[i];
  let prev = seed / period;
  res[period - 1] = prev;
  for (let i = period; i < n; i++) {
    prev = src[i] * k + prev * (1 - k);
    res[i] = prev;
  }
  return res;
}

/** Wilder's smoothing (RMA) — the average used inside RSI and ATR. */
function rma(src: Float64Array, n: number, period: number, start: number): Float64Array {
  const res = filled(n);
  if (n < start + period) return res;
  let sum = 0;
  for (let i = start; i < start + period; i++) sum += src[i];
  let prev = sum / period;
  res[start + period - 1] = prev;
  for (let i = start + period; i < n; i++) {
    prev = (prev * (period - 1) + src[i]) / period;
    res[i] = prev;
  }
  return res;
}

/** Population standard deviation over a rolling window. */
function rollingStdev(
  src: Float64Array,
  n: number,
  period: number,
  means: Float64Array,
): Float64Array {
  const res = filled(n);
  if (n < period) return res;
  // Sum of squares kept alongside the mean; a two-pass per window would be
  // O(n*period) and this runs on every parameter change.
  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    sumSq += src[i] * src[i];
    if (i >= period) sumSq -= src[i - period] * src[i - period];
    if (i >= period - 1) {
      const m = means[i];
      const variance = Math.max(0, sumSq / period - m * m);
      res[i] = Math.sqrt(variance);
    }
  }
  return res;
}

// ---------------------------------------------------------------------------
// Indicator implementations
// ---------------------------------------------------------------------------

function buildSMA(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const values = sma(s.close.data, n, Math.round(cfg.params.period));
  return {
    id: cfg.id,
    kind: 'sma',
    label: indicatorLabel(cfg),
    pane: 'price',
    lines: [{ key: 'sma', values, color: cfg.color, width: 1.25 }],
    range: null,
  };
}

function buildEMA(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const values = ema(s.close.data, n, Math.round(cfg.params.period));
  return {
    id: cfg.id,
    kind: 'ema',
    label: indicatorLabel(cfg),
    pane: 'price',
    lines: [{ key: 'ema', values, color: cfg.color, width: 1.25 }],
    range: null,
  };
}

function buildBB(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const period = Math.round(cfg.params.period);
  const mult = cfg.params.mult;
  const mid = sma(s.close.data, n, period);
  const sd = rollingStdev(s.close.data, n, period, mid);
  const upper = filled(n);
  const lower = filled(n);
  for (let i = 0; i < n; i++) {
    if (Number.isNaN(mid[i])) continue;
    upper[i] = mid[i] + sd[i] * mult;
    lower[i] = mid[i] - sd[i] * mult;
  }
  return {
    id: cfg.id,
    kind: 'bb',
    label: indicatorLabel(cfg),
    pane: 'price',
    lines: [
      { key: 'upper', values: upper, color: cfg.color, width: 1 },
      { key: 'mid', values: mid, color: cfg.color, width: 1, dashed: true },
      { key: 'lower', values: lower, color: cfg.color, width: 1 },
    ],
    // Very light: the envelope spans most of the plot, so anything heavier
    // tints the whole chart and fights the candles for attention.
    band: { upper: 'upper', lower: 'lower', fill: withAlpha(cfg.color, 0.03) },
    range: null,
  };
}

/**
 * Session-anchored VWAP.
 *
 * The anchor matters: a VWAP that accumulates from the left edge of whatever
 * happens to be loaded is not VWAP, it is an arbitrary weighted mean that
 * changes every time more history is fetched. Resetting on the UTC day (or
 * ISO week) boundary makes it reproducible.
 */
function buildVWAP(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const values = filled(n);
  const weekly = cfg.params.anchor === 1;
  const t = s.openTime.data;
  const h = s.high.data;
  const l = s.low.data;
  const c = s.close.data;
  const v = s.volume.data;

  let cumPV = 0;
  let cumV = 0;
  let bucket = -1;

  for (let i = 0; i < n; i++) {
    const d = new Date(t[i]);
    const key = weekly
      ? isoWeekKey(d)
      : d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
    if (key !== bucket) {
      bucket = key;
      cumPV = 0;
      cumV = 0;
    }
    const typical = (h[i] + l[i] + c[i]) / 3;
    cumPV += typical * v[i];
    cumV += v[i];
    values[i] = cumV > 0 ? cumPV / cumV : NaN;
  }

  return {
    id: cfg.id,
    kind: 'vwap',
    label: indicatorLabel(cfg),
    pane: 'price',
    lines: [{ key: 'vwap', values, color: cfg.color, width: 1.4 }],
    range: null,
  };
}

function isoWeekKey(d: Date): number {
  const t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const day = new Date(t).getUTCDay() || 7;
  const monday = t - (day - 1) * 86_400_000;
  return Math.floor(monday / 86_400_000);
}

function buildRSI(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const period = Math.round(cfg.params.period);
  const values = filled(n);
  if (n > period) {
    const close = s.close.data;
    const gains = new Float64Array(n);
    const losses = new Float64Array(n);
    for (let i = 1; i < n; i++) {
      const d = close[i] - close[i - 1];
      gains[i] = d > 0 ? d : 0;
      losses[i] = d < 0 ? -d : 0;
    }
    // Index 0 has no delta, so Wilder's averages start at index 1.
    const avgGain = rma(gains, n, period, 1);
    const avgLoss = rma(losses, n, period, 1);
    for (let i = 0; i < n; i++) {
      const g = avgGain[i];
      const lo = avgLoss[i];
      if (Number.isNaN(g) || Number.isNaN(lo)) continue;
      // A window with no down-closes gives RS = infinity; RSI is 100 there.
      values[i] = lo === 0 ? 100 : 100 - 100 / (1 + g / lo);
    }
  }
  return {
    id: cfg.id,
    kind: 'rsi',
    label: indicatorLabel(cfg),
    pane: 'sub',
    lines: [{ key: 'rsi', values, color: cfg.color, width: 1.3 }],
    range: [0, 100],
    guides: [30, 50, 70],
  };
}

function buildMACD(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const fast = Math.round(cfg.params.fast);
  const slow = Math.round(cfg.params.slow);
  const signalPeriod = Math.round(cfg.params.signal);

  const emaFast = ema(s.close.data, n, fast);
  const emaSlow = ema(s.close.data, n, slow);
  const macd = filled(n);
  for (let i = 0; i < n; i++) {
    if (Number.isNaN(emaFast[i]) || Number.isNaN(emaSlow[i])) continue;
    macd[i] = emaFast[i] - emaSlow[i];
  }

  // The signal line is an EMA *of the MACD line*, which only exists from the
  // slow period onward — feeding it the NaN prefix would poison the seed.
  const firstValid = macd.findIndex((x) => !Number.isNaN(x));
  const signal = filled(n);
  const hist = filled(n);
  if (firstValid >= 0 && n - firstValid >= signalPeriod) {
    const tail = macd.subarray(firstValid);
    const sig = ema(tail as Float64Array, tail.length, signalPeriod);
    for (let i = 0; i < sig.length; i++) {
      const at = firstValid + i;
      signal[at] = sig[i];
      if (!Number.isNaN(sig[i])) hist[at] = macd[at] - sig[i];
    }
  }

  return {
    id: cfg.id,
    kind: 'macd',
    label: indicatorLabel(cfg),
    pane: 'sub',
    lines: [
      { key: 'macd', values: macd, color: cfg.color, width: 1.3 },
      { key: 'signal', values: signal, color: C.info, width: 1.1 },
    ],
    histogram: {
      values: hist,
      upColor: C.up,
      downColor: C.down,
      upFade: withAlpha(C.up, 0.4),
      downFade: withAlpha(C.down, 0.4),
    },
    range: null,
    zeroLine: true,
  };
}

function buildATR(cfg: IndicatorConfig, s: KlineSeries): IndicatorOutput {
  const n = s.length;
  const period = Math.round(cfg.params.period);
  const tr = new Float64Array(n);
  const h = s.high.data;
  const l = s.low.data;
  const c = s.close.data;
  tr[0] = h[0] - l[0];
  for (let i = 1; i < n; i++) {
    // True range includes the gap from the previous close, which is the whole
    // reason ATR is not just the mean candle height.
    tr[i] = Math.max(h[i] - l[i], Math.abs(h[i] - c[i - 1]), Math.abs(l[i] - c[i - 1]));
  }
  const values = rma(tr, n, period, 1);
  return {
    id: cfg.id,
    kind: 'atr',
    label: indicatorLabel(cfg),
    pane: 'sub',
    lines: [{ key: 'atr', values, color: cfg.color, width: 1.3 }],
    range: null,
  };
}

const BUILDERS: Record<IndicatorKind, (c: IndicatorConfig, s: KlineSeries) => IndicatorOutput> = {
  sma: buildSMA,
  ema: buildEMA,
  bb: buildBB,
  vwap: buildVWAP,
  rsi: buildRSI,
  macd: buildMACD,
  atr: buildATR,
};

export function computeIndicator(cfg: IndicatorConfig, series: KlineSeries): IndicatorOutput {
  return BUILDERS[cfg.kind](cfg, series);
}

/** Hex or rgb colour with an alpha channel applied. */
export function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return color;
}

/**
 * Memoises indicator output against (series identity, series.seq, params).
 *
 * Without this, seven indicators over 600 bars recompute 60 times a second
 * for no reason — roughly a quarter of a million redundant float operations
 * per frame. With it, they recompute only when a candle actually changes.
 */
export class IndicatorCache {
  private entries = new Map<
    string,
    { seq: number; len: number; sig: string; series: KlineSeries; out: IndicatorOutput }
  >();

  get(cfg: IndicatorConfig, series: KlineSeries): IndicatorOutput {
    const sig = `${cfg.kind}:${JSON.stringify(cfg.params)}:${cfg.color}`;
    const hit = this.entries.get(cfg.id);
    if (
      hit &&
      hit.series === series &&
      hit.seq === series.seq &&
      hit.len === series.length &&
      hit.sig === sig
    ) {
      return hit.out;
    }
    const out = computeIndicator(cfg, series);
    this.entries.set(cfg.id, { seq: series.seq, len: series.length, sig, series, out });
    return out;
  }

  drop(id: string): void {
    this.entries.delete(id);
  }

  clear(): void {
    this.entries.clear();
  }
}
